import { useState, useEffect } from 'react';

/**
 * OfflineBanner — shows a slim banner when the browser goes offline,
 * auto-hides when connectivity returns. Non-intrusive and informational.
 */
export function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    function goOffline() { setOffline(true); setRecovering(false); }
    function goOnline() {
      setRecovering(true);
      // Brief delay so user sees the "reconnected" state
      setTimeout(() => { setOffline(false); setRecovering(false); }, 2000);
    }

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline && !recovering) return null;

  return (
    <div className={`fixed top-0 inset-x-0 z-70 flex items-center justify-center gap-2 px-4 py-1.5 text-2xs font-medium transition-colors duration-300 ${
      recovering
        ? 'bg-success/90 text-white'
        : 'bg-warn/90 text-white'
    }`}>
      {recovering ? (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          Back online — reconnecting
        </>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-pulse">
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
            <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0122.56 9" />
            <path d="M1.42 9a15.91 15.91 0 014.7-2.88" />
            <path d="M8.53 16.11a6 6 0 016.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
          You're offline — data may be stale
        </>
      )}
    </div>
  );
}
